import { createFileRoute, Link } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  FormLabel,
  FormWrapper,
  FormWrapperControl,
  FormWrapperField,
  FormWrapperItem,
  FormWrapperMessage,
  Spinner,
  Textarea,
} from '@opaz/ui-components';
import UploadFilled from '@/assets/icons/UploadFilled';
import DeleteForeveFilled from '@/assets/icons/DeleteForeveFilled';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Languages } from '@/lib/constants';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { SubNavContainer } from '@/components/sub-nav-container';
import { useEffect } from 'react';

import { useAnalytics } from '@/hooks/useAnalytics';

export const Route = createFileRoute('/feedback/')({
  component: FeedbackPage,
  context: () => {
    return {
      getBreadcrumbData: () => ({
        id: null,
        translationKey: null,
      }),
    };
  },
  head: () => {
    return {
      meta: [
        {
          title: 'Feedback - OPAZ E-Service Portal',
        },
      ],
    };
  },
});

export default function FeedbackPage() {
  const { t, i18n } = useTranslation();
  const analytics = useAnalytics();

  const feedbackSchema = z.object({
    message: z.string().min(1, t('feedback.validation.messageRequired')),
    file: z
      .any()
      .refine(
        (file) => {
          if (!file) return true;
          return ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type) && file.size <= 5 * 1024 * 1024;
        },
        { message: 'Only PDF, JPG, or PNG files under 5MB are allowed' },
      )
      .optional(),
  });

  type FeedbackFormValues = z.infer<typeof feedbackSchema>;

  const form = useForm({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      message: '',
      file: undefined,
    },
  });

  const file = form.watch('file');

  const mutation = useMutation({
    mutationFn: async (formValues: FeedbackFormValues) => {
      let uploadedFile;

      if (formValues.file) {
        const formData = new FormData();
        formData.append('files', formValues.file);

        const uploadResponse = await axios.post(`${import.meta.env.VITE_STRAPI_URL}/upload`, formData);

        uploadedFile = uploadResponse.data[0];
      }

      const feedbackData = {
        data: {
          feedback: formValues.message,
          ...(uploadedFile?.id && { files: uploadedFile.id }),
        },
      };

      const response = await axios.post(`${import.meta.env.VITE_STRAPI_URL}/feedbacks`, feedbackData);
      return response.data;
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const onSubmit = (data: FeedbackFormValues) => {
    // Track form submission attempt
    if (analytics) {
      analytics.trackFeedbackSubmitted('feedback', 'feedback-form', 'user_feedback');
    }

    mutation.mutate(data, {
      onSuccess: () => {
        if (analytics) {
          analytics.trackSurveySubmit({ mesage: data.message });
        }
      },
      onError: (error) => {
        if (analytics) {
          analytics.trackSurveyDecline();
          analytics.trackApiError('feedback-submit', 500);
        }

        console.error('Feedback submission error:', error);
      },
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    form.setValue('file', selectedFile);
  };

  const handleFileRemove = () => {
    form.setValue('file', undefined);
  };

  const fileInputId = 'file-upload';

  return (
    <>
      <SubNavContainer showOneStepBreadcrumbs>
        <div className="max-w-container-960">
          <h1 className="text-3xl font-bold mb-5 text-foreground">{t('feedback.title')}</h1>
          <p className="text-xl font-normal text-foreground">{t('feedback.subtitle')}</p>
        </div>
      </SubNavContainer>

      <div className="max-w-container-960 mx-auto mt-8 px-6 lg:px-0">
        {!mutation.isSuccess ? (
          <FormWrapper {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 mb-14" aria-label="Feedback Form">
              <FormWrapperField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormWrapperItem>
                    <FormLabel htmlFor="message-textarea" className="font-bold text-base">
                      {t('feedback.form.messageLabel')}
                    </FormLabel>
                    <FormWrapperControl>
                      <Textarea
                        {...field}
                        id="message-textarea"
                        aria-required="true"
                        className="min-h-[120px] rounded-xl border-gray-500 w-8/12 mt-3 "
                      />
                    </FormWrapperControl>
                    <FormWrapperMessage className="mt-3" />
                  </FormWrapperItem>
                )}
              />

              <FormWrapperField
                control={form.control}
                name="file"
                render={() => (
                  <FormWrapperItem>
                    <FormLabel htmlFor={fileInputId} className="font-bold text-base mb-3">
                      {t('feedback.form.uploadLabel')}{' '}
                      <span className="font-normal">{t('feedback.form.uploadOptional')}</span>
                    </FormLabel>
                    <p className="text-muted-foreground" id="file-description">
                      {t('feedback.form.uploadNote')}
                    </p>

                    {!file && (
                      <>
                        <input
                          type="file"
                          id={fileInputId}
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleFileChange}
                          className="sr-only"
                          aria-describedby="file-description"
                        />
                        <label htmlFor={fileInputId}>
                          <div
                            className="inline-flex font-normal items-center gap-2 mt-4 cursor-pointer text-brand-xhigh border border-brand-xhigh rounded-full px-4 py-2 hover:bg-accent"
                            role="button"
                            tabIndex={0}
                            aria-label={t('feedback.form.chooseFile')}
                          >
                            <UploadFilled aria-hidden="true" />
                            <span>{t('feedback.form.chooseFile')}</span>
                          </div>
                        </label>
                      </>
                    )}

                    {file && (
                      <div
                        style={{ marginTop: '16px' }}
                        className={cn(
                          'mt-4 flex w-fit items-center justify-between bg-muted px-4 py-3 rounded-xl space-x-6',
                          i18n.language === Languages.AR && 'flex-row-reverse',
                        )}
                      >
                        <div>
                          <p className="text-sm font-bold text-foreground">{file.name}</p>
                          <p className="text-xs text-muted-foreground">{(file.size / (1024 * 1024)).toFixed(1)} MB</p>
                        </div>
                        <button
                          type="button"
                          aria-label={t('feedback.form.removeFile')}
                          onClick={handleFileRemove}
                          className="p-1 focus:outline-none focus:ring-2 focus:ring-brand-xhigh rounded-full"
                        >
                          <DeleteForeveFilled className="text-brand-xhigh" aria-hidden="true" />
                        </button>
                      </div>
                    )}

                    <FormWrapperMessage />
                  </FormWrapperItem>
                )}
              />

              {mutation.isError && (
                <p className="text-sm text-red-500 mt-2" role="alert">
                  {mutation.error?.message || t('common.errorMessage')}
                </p>
              )}

              <Button
                type="submit"
                disabled={form.formState.isSubmitting || mutation.isPending}
                className="inline-flex items-center justify-center"
                aria-busy={form.formState.isSubmitting || mutation.isPending}
              >
                {mutation.isPending ? (
                  <>
                    <Spinner className="animate-spin" aria-hidden="true" />
                    {t('feedback.form.submitting')}
                  </>
                ) : (
                  t('feedback.form.submit')
                )}
              </Button>
            </form>
          </FormWrapper>
        ) : (
          <div className="mb-14" role="status" aria-live="polite">
            <h2 className="text-3xl font-bold mb-5 text-foreground">{t('feedback.thankYou.title')}</h2>
            <p className="text-xl font-normal text-foreground max-w-2xl">{t('feedback.thankYou.description')}</p>
            <Link to="/">
              <Button
                className="rounded-full px-6 mt-8"
                onClick={() => analytics?.trackNavigationClick('Return Home from Feedback')}
              >
                {t('feedback.thankYou.returnHome')}
              </Button>
            </Link>
          </div>
        )}
      </div>
    </>
  );
}
